import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import Carousel from "react-material-ui-carousel";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { bookReleaseAPI } from "../api/api";
import { BookRelease } from "../types/book";

const Home: React.FC = () => {
  const [featuredReleases, setFeaturedReleases] = useState<BookRelease[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchFeaturedReleases = async () => {
      try {
        setLoading(true);
        const releases = await bookReleaseAPI.getFeaturedReleases();
        setFeaturedReleases(releases);
        setError(null);
      } catch (err) {
        console.error("Error fetching featured releases:", err);
        setError("Failed to load featured releases. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedReleases();
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
          gap: 3,
        }}
      >
        <Box sx={{ position: "relative" }}>
          <CircularProgress
            size={60}
            thickness={4}
            sx={{
              color: "#C9A961",
              animation: "pulse 1.5s ease-in-out infinite",
              "@keyframes pulse": {
                "0%": { opacity: 1 },
                "50%": { opacity: 0.5 },
                "100%": { opacity: 1 },
              },
            }}
          />
        </Box>
        <Typography
          variant="h6"
          sx={{
            color: "text.primary",
            opacity: 0.7,
            textAlign: "center",
          }}
        >
          Loading featured releases...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          p: 4,
          minHeight: "60vh",
          justifyContent: "center",
        }}
      >
        <Alert
          severity="error"
          sx={{
            maxWidth: "600px",
            backgroundColor: "rgba(217, 83, 79, 0.1)",
            border: "1px solid rgba(217, 83, 79, 0.3)",
            borderRadius: 2,
            "& .MuiAlert-icon": {
              color: "#D9534F",
            },
          }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            Oops! Something went wrong
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            {error}
          </Typography>
        </Alert>
        <Button
          onClick={() => window.location.reload()}
          variant="outlined"
          sx={{ mt: 3 }}
        >
          Try Again
        </Button>
      </Box>
    );
  }

  if (featuredReleases.length === 0) {
    return (
      <Box>
        {/* Hero Section - Same as above */}
        <Box
          sx={{
            position: "relative",
            py: { xs: 6, sm: 8, md: 12 },
            px: { xs: 2, sm: 3, md: 4 },
            textAlign: "center",
            maxWidth: "1000px",
            mx: "auto",
            minHeight: { xs: "400px", md: "500px" },
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h1"
            component="h1"
            sx={{
              mb: 3,
              background:
                "linear-gradient(135deg, #C9A961 0%, #E8D5A3 50%, #FDFCF8 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            The Shelf Saga
          </Typography>

          <Typography
            variant="h5"
            sx={{
              mb: 5,
              color: "text.primary",
              opacity: 0.9,
              fontWeight: 400,
              maxWidth: "600px",
              lineHeight: 1.6,
            }}
          >
            Never miss a special edition book release again. Stay updated with
            the latest literary treasures.
          </Typography>

          <Button
            component={RouterLink}
            to="/calendar"
            variant="contained"
            size="large"
            sx={{
              fontSize: "1.125rem",
              px: 5,
              py: 2,
            }}
          >
            Explore Release Calendar
          </Button>
        </Box>

        {/* No Releases Message */}
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            px: 4,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              mb: 2,
              color: "text.primary",
              opacity: 0.7,
            }}
          >
            No Featured Releases Yet
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.primary",
              opacity: 0.6,
              maxWidth: "500px",
              mx: "auto",
            }}
          >
            Check back soon for exciting new book releases, or explore our
            calendar to see what's coming up.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* Hero Title */}
      <Box
        sx={{
          position: "relative",
          py: { xs: 4, sm: 5, md: 6 },
          px: { xs: 2, sm: 3, md: 4 },
          textAlign: "center",
          maxWidth: "1000px",
          mx: "auto",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: "20%",
            left: "10%",
            width: "200px",
            height: "200px",
            background:
              "radial-gradient(circle, rgba(201, 169, 97, 0.1) 0%, transparent 70%)",
            borderRadius: "50%",
            animation: "float 6s ease-in-out infinite",
            "@keyframes float": {
              "0%, 100%": { transform: "translateY(0px) rotate(0deg)" },
              "50%": { transform: "translateY(-20px) rotate(180deg)" },
            },
          },
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: "20%",
            right: "10%",
            width: "150px",
            height: "150px",
            background:
              "radial-gradient(circle, rgba(201, 169, 97, 0.08) 0%, transparent 70%)",
            borderRadius: "50%",
            animation: "float-reverse 8s ease-in-out infinite",
            "@keyframes float-reverse": {
              "0%, 100%": { transform: "translateY(0px) rotate(0deg)" },
              "50%": { transform: "translateY(15px) rotate(-180deg)" },
            },
          },
        }}
      >
        <Typography
          variant="h1"
          component="h1"
          sx={{
            position: "relative",
            zIndex: 1,
            mb: 2,
            background:
              "linear-gradient(135deg, #C9A961 0%, #E8D5A3 50%, #FDFCF8 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            textShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            animation: "fadeInUp 1s ease-out",
            "@keyframes fadeInUp": {
              from: { opacity: 0, transform: "translateY(30px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          The Shelf Saga
        </Typography>
      </Box>

      {/* Featured Releases Carousel */}
      <Box
        sx={{
          px: { xs: 0, sm: 3, md: 4 },
          mx: { xs: 0, lg: 10 },
          position: "relative",
          mb: 6,
        }}
      >
        <Carousel
          animation="slide"
          duration={600}
          navButtonsAlwaysVisible
          navButtonsProps={{
            style: {
              backgroundColor: "rgba(201, 169, 97, 0.8)",
              color: "#0B0E26",
              borderRadius: "12px",
              width: "48px",
              height: "48px",
              border: "2px solid rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(8px)",
            },
          }}
          indicatorContainerProps={{
            style: {
              marginTop: "24px",
              textAlign: "center",
            },
          }}
          indicatorIconButtonProps={{
            style: {
              color: "rgba(201, 169, 97, 0.4)",
              margin: "0 8px",
            },
          }}
          activeIndicatorIconButtonProps={{
            style: {
              color: "#C9A961",
            },
          }}
          sx={{
            maxWidth: "1400px",
            mx: "auto",
            "& .carousel-root": {
              borderRadius: "16px",
              overflow: "hidden",
            },
          }}
        >
          {featuredReleases.map((release, index) => (
            <Box key={release._id} sx={{ p: 2 }}>
              <Card
                elevation={0}
                sx={{
                  height: "auto",
                  minHeight: "600px",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 4,
                  background:
                    "linear-gradient(135deg, rgba(20, 24, 50, 0.9) 0%, rgba(20, 24, 50, 0.7) 100%)",
                  border: "1px solid rgba(201, 169, 97, 0.2)",
                  boxShadow: "0 8px 40px rgba(0, 0, 0, 0.15)",
                  backdropFilter: "blur(12px)",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                  animation: `fadeInScale 0.6s ease-out ${index * 0.1}s both`,
                  "@keyframes fadeInScale": {
                    from: {
                      opacity: 0,
                      transform: "scale(0.95) translateY(20px)",
                    },
                    to: {
                      opacity: 1,
                      transform: "scale(1) translateY(0)",
                    },
                  },
                  "&:hover": {
                    transform: "translateY(-8px) scale(1.02)",
                    boxShadow:
                      "0 20px 60px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(201, 169, 97, 0.3)",
                    "& .book-cover": {
                      transform: "scale(1.05)",
                    },
                    "& .book-title": {
                      color: "#E8D5A3",
                    },
                  },
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background:
                      "radial-gradient(ellipse at center, rgba(201, 169, 97, 0.05) 0%, transparent 70%)",
                    pointerEvents: "none",
                  },
                }}
                onClick={() => navigate(`/releases/${release._id}`)}
              >
                <Box
                  sx={{
                    position: "relative",
                    height: "400px", // Increased height for better book proportions
                    overflow: "hidden",
                    borderRadius: "16px 16px 0 0",
                    background:
                      "linear-gradient(135deg, rgba(201, 169, 97, 0.05) 0%, rgba(11, 14, 38, 0.1) 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CardMedia
                    component="img"
                    image={
                      release.coverImage ||
                      `https://source.unsplash.com/random/400x600?book,${encodeURIComponent(release.title)}`
                    }
                    alt={release.title}
                    className="book-cover"
                    sx={{
                      height: "100%",
                      width: "100%",
                      objectFit: "contain", // Changed from 'cover' to 'contain' to show full image
                      objectPosition: "center",
                      backgroundColor: "rgba(11, 14, 38, 0.3)", // Subtle background for letterboxing
                      transition: "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                      filter: "brightness(1.1) saturate(1.2)",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: "40%",
                      background:
                        "linear-gradient(transparent, rgba(11, 14, 38, 0.8))",
                      pointerEvents: "none",
                    }}
                  />
                </Box>
                <CardContent
                  sx={{
                    flexGrow: 1,
                    py: 3,
                    px: 6,
                    position: "relative",
                    zIndex: 1,
                    minHeight: "280px", // Fixed minimum height to prevent layout shifts
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography
                    variant="h4"
                    className="book-title"
                    sx={{
                      fontWeight: 700,
                      color: "text.primary",
                      mb: 1,
                      transition: "color 0.3s ease",
                      lineHeight: 1.3,
                    }}
                  >
                    {release.title}
                  </Typography>

                  <Typography
                    variant="h6"
                    sx={{
                      color: "text.secondary",
                      mb: 3,
                      fontWeight: 400,
                      opacity: 0.9,
                    }}
                  >
                    by {release.author}
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      mb: 4,
                      color: "text.primary",
                      opacity: 0.8,
                      lineHeight: 1.6,
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      minHeight: "72px", // 3 lines × 24px line height = consistent description height
                      flex: "0 0 auto", // Don't allow this to shrink or grow
                    }}
                  >
                    {release.description}
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: "text.secondary",
                        fontWeight: 500,
                        mb: 1,
                      }}
                    >
                      {release.publisher.name}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.primary",
                        opacity: 0.7,
                        fontSize: "0.9rem",
                        mb: 1,
                      }}
                    >
                      {(() => {
                        const utcDate = dayjs(release.releaseDate).utc();
                        const isTimeSet =
                          utcDate.hour() !== 0 || utcDate.minute() !== 0;
                        const localDate = utcDate.tz(dayjs.tz.guess());
                        return isTimeSet
                          ? `${localDate.format("MMMM D, YYYY[,] h:mm A")} ${dayjs.tz.guess().split("/")[1].replace("_", " ")}`
                          : localDate.format("MMMM D, YYYY");
                      })()}
                    </Typography>

                    {release.price && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          fontWeight: 600,
                          fontSize: "1rem",
                        }}
                      >
                        {release.price} {release.currency}
                      </Typography>
                    )}
                  </Box>
                  {/* Spacer to push content to bottom */}
                  <Box sx={{ flexGrow: 1 }} />

                  <Box
                    sx={{
                      mt: "auto", // Push to bottom of flex container
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        minHeight: "32px", // Fixed height to prevent layout shift
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1.5,
                          flexWrap: "wrap",
                          flex: 1,
                        }}
                      >
                        <Chip
                          label={release.editionType}
                          sx={{
                            background:
                              "linear-gradient(135deg, rgba(201, 169, 97, 0.2), rgba(232, 213, 163, 0.2))",
                            color: "#E8D5A3",
                            border: "1px solid rgba(201, 169, 97, 0.3)",
                            fontWeight: 600,
                            fontSize: "0.8rem",
                          }}
                        />
                        <Chip
                          label={release.status}
                          sx={{
                            backgroundColor:
                              release.status === "Available"
                                ? "rgba(92, 184, 92, 0.2)"
                                : "rgba(201, 169, 97, 0.15)",
                            color:
                              release.status === "Available"
                                ? "#7DC17D"
                                : "#C9A961",
                            border: `1px solid ${
                              release.status === "Available"
                                ? "rgba(125, 193, 125, 0.3)"
                                : "rgba(201, 169, 97, 0.3)"
                            }`,
                            fontWeight: 500,
                          }}
                        />
                      </Box>

                      {release.preOrderLink && (
                        <Button
                          href={release.preOrderLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="outlined"
                          size="small"
                          sx={{
                            borderColor: "rgba(201, 169, 97, 0.5)",
                            color: "#C9A961",
                            fontSize: "0.875rem",
                            transform: "translate(0px, -20px)",
                            flexShrink: 0, // Prevent button from shrinking
                            "&:hover": {
                              borderColor: "#C9A961",
                              backgroundColor: "rgba(201, 169, 97, 0.1)",
                              transform: "translateY(-2px)",
                            },
                          }}
                        >
                          Pre-order Now
                        </Button>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          ))}
        </Carousel>
      </Box>

      {/* Description Section */}
      <Box
        sx={{
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 4, sm: 6, md: 8 },
          textAlign: "center",
          maxWidth: "800px",
          mx: "auto",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            mb: 3,
            color: "text.primary",
            opacity: 0.9,
            fontWeight: 400,
            lineHeight: 1.6,
          }}
        >
          Never miss a special edition book release again. Stay updated with the
          latest literary treasures.
        </Typography>

        <Typography
          variant="body1"
          sx={{
            mb: 5,
            color: "text.primary",
            opacity: 0.8,
            lineHeight: 1.6,
            fontSize: "1.1rem",
          }}
        >
          Discover handpicked special editions and limited releases from your
          favorite publishers, all curated to help bibliophiles find their next
          treasured addition.
        </Typography>

        <Button
          component={RouterLink}
          to="/calendar"
          variant="contained"
          size="large"
          sx={{
            fontSize: "1.125rem",
            px: 5,
            py: 2,
            borderRadius: 2,
            background: "linear-gradient(135deg, #C9A961 0%, #E8D5A3 100%)",
            color: "#0B0E26",
            fontWeight: 600,
            boxShadow: "0 8px 32px rgba(201, 169, 97, 0.3)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              transform: "translateY(-3px) scale(1.02)",
              boxShadow: "0 12px 48px rgba(201, 169, 97, 0.4)",
              background: "linear-gradient(135deg, #E8D5A3 0%, #C9A961 100%)",
            },
            "&:active": {
              transform: "translateY(-1px) scale(0.98)",
            },
          }}
        >
          Explore Release Calendar
        </Button>
      </Box>
    </Box>
  );
};

export default Home;
